import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  Input,
  Output,
  forwardRef,
  ViewChild,
  EventEmitter,
  ElementRef,
  ChangeDetectorRef,
  SimpleChanges,
  Injector,
} from "@angular/core";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

// models
import { ICFixedCcyModel } from "../order-entry/interface/controls/models/bfx-interface-controls.models";
import { ICNInputControlState } from "../order-entry/interface/controls/models/bfx-interface-controls.enums";
import { BfxInputDirective } from "shared/base-components/bfx-input";
import { SizeMixin } from "shared/base-components/mixins/size";
import { InputLabelStateMixin } from "shared/base-components/mixins/label-state";
import { DisabledMixin } from "shared/base-components/mixins/disabled";
import { ComponentMixin } from "shared/base-components/mixins/mixin";
import { EmphasisMixin } from "shared/base-components/mixins/emphasis";
import { CcyPairLookupService, CcyPairPromiseLookupService } from "shared/cubiq-services";
import { CurrencyPairImpl, TermBaseEntry } from "shared/cubiq-model-multi-asset";

export const KEYBOARD_SPACE_KEY_CODE = 32;
export const KEYBOARD_ENTER_KEY_CODE = 13;
export const FIXED_CCY_EMPTY_VALUE = "-";

export const FIXED_CCY_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => ICFixedCcyComponent),
  multi: true,
};

const Mixins = SizeMixin(EmphasisMixin(InputLabelStateMixin(DisabledMixin(ComponentMixin))));
@Component({
  selector: "ic-fixed-ccy",
  templateUrl: "./html/ic-fixed-ccy.html",
  providers: [FIXED_CCY_VALUE_ACCESSOR],
  standalone: false,
})
export class ICFixedCcyComponent extends Mixins implements OnChanges, ControlValueAccessor {
  /***************************************/
  /* INPUTS */
  @Input() _inputId: string;
  @Input("ccyPair") _ccyPair: string;
  /* set this flag to true when the component is used in order entry defaults - styling change*/
  @Input("orderEntryDefaults") _modeDefaults: boolean = false;
  @Input("label") label: string = null;
  @ViewChild("button", { static: true }) private _button: ElementRef;

  /***************************************/
  /* DATA */
  //public value: ICFixedCcyModel = null;
  public fixedCcy: string = FIXED_CCY_EMPTY_VALUE;
  public ccyPair: string = null;
  public base: string = FIXED_CCY_EMPTY_VALUE;
  public nonBase: string = FIXED_CCY_EMPTY_VALUE;
  public highlightFixed: boolean = false;

  public locked: boolean = false;

  /** Can enter in Term (nonBase), Base, or both */
  private tradableTypes: TermBaseEntry = TermBaseEntry.BOTH;

  /** Is locked because toggle from fixed is disabled? */
  private toggleLocked: boolean = true;

  private currencyPairLookup: CcyPairLookupService;
  /**********************************************************************/
  /* CONSTRUCTOR + LIFE CYCLE HOOKS */
  constructor(
    ccyPairService: CcyPairPromiseLookupService,
    injector: Injector,
    public inputDirective: BfxInputDirective,
  ) {
    super(injector);
    this.size = "sm";
    this.currencyPairLookup = ccyPairService.getBase();
  }

  ngOnChanges(changes: SimpleChanges) {
    for (let propName in changes) {
      if (propName === "_ccyPair") {
        this.updateCcyPair(this._ccyPair);
      }
    }
  }

  /**********************************************************************/
  /* CONTROL VALUE ACCESSOR */

  onModelChange: Function = (value: any) => {};
  onModelTouched: Function = () => {};

  writeValue(value: any): void {}

  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  onChange(value: any): void {
    if (value instanceof ICFixedCcyModel || value === null) {
      this.onModelChange(value);
    } else {
      this.onModelChange(null);
    }
  }
  /**********************************************************************/
  /* PUBLIC METHODS */

  public updateCcyPair(value: string, triggerChange: boolean = true) {
    value = typeof value === "string" ? value.toUpperCase() : value;
    if (value !== this.ccyPair) {
      if (typeof value === "string" && value.length === 6) {
        this.ccyPair = value;
        this.base = this.ccyPair.substring(0, 3);
        this.nonBase = this.ccyPair.substring(3, 6);

        this.setupTermEntry();

        const newFixed: string =
          this.tradableTypes === TermBaseEntry.TERM ? this.nonBase : this.base;
        this.updateFixed(newFixed);

        if (triggerChange) {
          this.onChange(this.compose());
        }
      } else {
        this.reset();
      }
    }
  }

  public load(value: ICFixedCcyModel, triggerChange: boolean = true): void {
    if (
      value instanceof ICFixedCcyModel &&
      typeof value.ccyPair === "string" &&
      value.ccyPair.length === 6 &&
      typeof value.ccy === "string" &&
      value.ccy.length === 3 &&
      (value.ccy == value.ccyPair.substring(0, 3) || value.ccy == value.ccyPair.substring(3, 6))
    ) {
      this.ccyPair = value.ccyPair.toUpperCase();
      this.base = this.ccyPair.substring(0, 3);
      this.nonBase = this.ccyPair.substring(3, 6);
      this.setupTermEntry();

      this.updateFixed(value.ccy.toUpperCase());

      if (triggerChange) {
        this.onChange(this.compose());
      }
    } else {
      logger.out.error(`ERROR: Failed to load FixedCcy value: %O`, value);
    }
  }

  public reset(triggerChange: boolean = true): void {
    this.ccyPair = null;

    this.tradableTypes = TermBaseEntry.BOTH;
    this.base = FIXED_CCY_EMPTY_VALUE;
    this.nonBase = FIXED_CCY_EMPTY_VALUE;
    this.locked = false;
    this.updateFixed(FIXED_CCY_EMPTY_VALUE);

    if (triggerChange) {
      this.onChange(this.compose());
    }
  }

  public lock(): void {
    this.setLocked(true);
  }

  public unlock(): void {
    this.setLocked(false);
  }

  private setLocked(value: boolean) {
    this.locked = value;
    this.disabled = this.locked || this.toggleLocked;
    this.inputLabelState = this.disabled ? "disabled" : "enabled";
  }

  public toggleCcy($event: any): void {
    if (
      typeof this.base === "string" &&
      this.base !== FIXED_CCY_EMPTY_VALUE &&
      this.base.length === 3 &&
      typeof this.nonBase === "string" &&
      this.nonBase !== FIXED_CCY_EMPTY_VALUE &&
      this.nonBase.length === 3
    ) {
      if (this.fixedCcy === this.base) {
        this.updateFixed(this.nonBase);
      } else if (this.fixedCcy === this.nonBase) {
        this.updateFixed(this.base);
      } else {
        this.updateFixed(this.base);
      }
    } else {
      this.updateFixed(FIXED_CCY_EMPTY_VALUE);
    }
    this.onChange(this.compose());
  }

  public keyup(event: any): void {
    if (event != null && event.keyCode === KEYBOARD_SPACE_KEY_CODE) {
      setTimeout(() => this._button.nativeElement.focus(), 0);
    }
  }

  /**********************************************************************/
  /* PRIVATE METHODS */

  private compose(): ICFixedCcyModel {
    if (
      this.ccyPair == null ||
      this.ccyPair == FIXED_CCY_EMPTY_VALUE ||
      this.fixedCcy == null ||
      this.fixedCcy == FIXED_CCY_EMPTY_VALUE
    ) {
      return null;
    } else if (
      typeof this.ccyPair == "string" &&
      this.ccyPair.length === 6 &&
      typeof this.fixedCcy === "string" &&
      (this.fixedCcy == this.ccyPair.substring(0, 3) ||
        this.fixedCcy == this.ccyPair.substring(3, 6))
    ) {
      return new ICFixedCcyModel(this.fixedCcy, this.ccyPair);
    } else {
      logger.out.error(
        `ERROR: Failed to compose Fixed-Ccy model. ccyPair: "${this.ccyPair}" | fixedCcy "${this.fixedCcy}"`,
      );
      return null;
    }
  }

  /**
   * bind properties required by MixinDirective change detection
   */
  get bfxInput(): Record<string, any> {
    return this.inputDirective.directiveHost(this);
  }

  /**
   * Single point to update the value and refresh change detection
   * @param value
   */
  private updateFixed(value: string) {
    this.fixedCcy = value;
    this.highlightFixed = value !== FIXED_CCY_EMPTY_VALUE && value === this.nonBase;
    this.updateToggleForTermEntry();
  }

  /**
   * Determine the tradable types based on currently selected pair - will invert termBaseEntry for inverted pairs
   */
  private setupTermEntry() {
    let invert = false;
    if (
      this.base &&
      this.nonBase &&
      this.base !== FIXED_CCY_EMPTY_VALUE &&
      this.nonBase !== FIXED_CCY_EMPTY_VALUE
    ) {
      let ccyPair = this.currencyPairLookup.getCcyPair(this.base, this.nonBase, false);
      if (!ccyPair) {
        invert = true;
        ccyPair = this.currencyPairLookup.getCcyPair(this.nonBase, this.base, false);
      }

      if (ccyPair) {
        let termEntry: TermBaseEntry = ccyPair.termBaseEntry;
        if (termEntry == null) {
          logger.out.warn(`icfc-c - No Term Entry found for  for ${this.base}${this.nonBase}`);
          termEntry = TermBaseEntry.BOTH;
        } else if (termEntry !== TermBaseEntry.BOTH && invert) {
          termEntry = termEntry === TermBaseEntry.BASE ? TermBaseEntry.TERM : TermBaseEntry.BASE;
        }
        this.tradableTypes = termEntry;
      } else {
        logger.out.warn(`icfc-c - Cannot find currency pair for ${this.base}${this.nonBase}`);
      }
    }
  }

  /**
   * Determine whether toggle action is allowed based on current values of tradableTypes and fixed
   */
  private updateToggleForTermEntry() {
    if (
      this.base &&
      this.nonBase &&
      this.base !== FIXED_CCY_EMPTY_VALUE &&
      this.nonBase !== FIXED_CCY_EMPTY_VALUE
    ) {
      switch (this.tradableTypes) {
        case TermBaseEntry.TERM:
          this.toggleLocked = this.fixedCcy === this.nonBase;
          break;
        case TermBaseEntry.BASE:
          this.toggleLocked = this.fixedCcy === this.base;
          break;
        case TermBaseEntry.BOTH:
          this.toggleLocked = false;
          break;
        default:
          this.toggleLocked = true;
          break;
      }
    } else {
      this.toggleLocked = true;
    }
    this.disabled = this.locked || this.toggleLocked;
    this.inputLabelState = this.disabled ? "disabled" : "enabled";
  }
}
